import React, {useEffect, useState} from 'react'
import {Box, Grid, Typography} from '@mui/material'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import {LoadingButton} from '@mui/lab'
import {useSnackbar} from 'notistack'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import {FormProvider, RHFSelectField, RHFTextField} from '../../../common/hook-form'
import {getMerchant} from '../../../helpers/ReduxHelpers'
import {get, apiConstants, patch} from '../../../common/constants'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, PRIMARY_BLUE} from '../../../styles/foundation/colors'

const IFSC_RE = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/
const NAME_RE = /^[A-Za-z][A-Za-z\s.&-]*$/

const schema = Yup.object().shape({
  beneficiaryName: Yup.string()
    .required('Beneficiary name is required')
    .matches(NAME_RE, 'Use letters only (no digits/symbols)')
    .min(2, 'Too short').max(60, 'Too long'),
  bankName: Yup.string()
    .required('Bank name is required')
    .matches(NAME_RE, 'Use letters only')
    .min(2, 'Too short').max(60, 'Too long'),
  accountNumber: Yup.string()
    .required('Account number is required')
    .matches(/^\d{9,18}$/, 'Enter a valid account number (9–18 digits)'),
  confirmAccountNumber: Yup.string()
    .required('Please re-enter the account number')
    .oneOf([Yup.ref('accountNumber')], 'Account numbers do not match'),
  accountType: Yup.string().required('Account type is required'),
  ifscCode: Yup.string()
    .required('IFSC code is required')
    .matches(IFSC_RE, 'Invalid IFSC — format like HDFC0001234'),
})

const sectionLabelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5}

export default function BankDetails() {
  const [formLoading, setFormLoading] = useState(false)
  const [reload, setReload] = useState(1)
  const {enqueueSnackbar} = useSnackbar()

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      beneficiaryName: '', bankName: '', accountNumber: '',
      confirmAccountNumber: '', accountType: 'saving', ifscCode: '',
    },
  })
  const {handleSubmit, setValue} = methods

  const merchant = getMerchant()
  const merchantId = merchant?.id || ''

  useEffect(() => {
    if (!merchantId) return
    get(`${apiConstants.BASE_LOCAL}${apiConstants.GET_MERCHANT}/${merchantId}`)
      .then((res) => {
        const b = res.data?.bank_details || {}
        setValue('beneficiaryName', b.beneficiaryName || '')
        setValue('bankName', b.bankName || '')
        setValue('accountNumber', b.accountNumber || '')
        setValue('confirmAccountNumber', b.accountNumber || '')
        setValue('accountType', (b.accountType || 'saving').toLowerCase())
        setValue('ifscCode', b.ifscCode || '')
      })
      .catch((err) => console.error(err))
  }, [reload, merchantId, setValue])

  const onSubmit = (data) => {
    setFormLoading(true)
    const payload = {
      beneficiaryName: data.beneficiaryName.trim(),
      bankName: data.bankName.trim(),
      accountNumber: data.accountNumber.trim(),
      accountType: data.accountType,
      ifscCode: data.ifscCode.trim().toUpperCase(),
    }
    patch(`${apiConstants.BASE_LOCAL}${apiConstants.UPDATE_MERCHANT}`, payload)
      .then(() => {
        enqueueSnackbar('Bank details updated', {variant: 'success'})
        setReload((r) => r + 1)
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar('Bank details could not be updated', {variant: 'error'})
      })
      .finally(() => setFormLoading(false))
  }

  return (
    <Box>
      <Typography sx={sectionLabelSx}>Account Details</Typography>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFTextField name='beneficiaryName' label='Beneficiary Name' mandatory size='small' />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField name='bankName' label='Bank Name' mandatory size='small' />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField name='accountNumber' label='Account Number' mandatory size='small'
              inputProps={{inputMode: 'numeric', maxLength: 18}} />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField name='confirmAccountNumber' label='Confirm Account Number' mandatory size='small'
              inputProps={{inputMode: 'numeric', maxLength: 18}}
              onPaste={(e) => e.preventDefault()} />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFSelectField name='accountType' label='Account Type' width='100%' size='small'
              options={[{label: 'Savings Account', value: 'saving'}, {label: 'Current Account', value: 'current'}]} />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField name='ifscCode' label='IFSC Code' mandatory size='small'
              placeholder='HDFC0001234'
              inputProps={{maxLength: 11, style: {textTransform: 'uppercase'}}} />
          </Grid>
          <Grid item xs={12}>
            <LoadingButton type='submit' loading={formLoading} variant='contained' startIcon={<SaveOutlinedIcon />}
              sx={{fontFamily: FONT, textTransform: 'none', borderRadius: '8px', bgcolor: PRIMARY_BLUE, px: 3, py: 1, mt: 0.5}}>
              Save Bank Details
            </LoadingButton>
          </Grid>
        </Grid>
      </FormProvider>
    </Box>
  )
}
